import Api from '../utils/api';
import { IStorefront } from '../interfaces/storefront.interface';
import { StorefrontCollectionLayout } from '../enums/storefront-collection-layout.enum';

export interface IStorefrontCollectionInput {
  id?: string;
  name: string;
  description?: string;
  layout: StorefrontCollectionLayout;
  order: number;
  productIds: string[];
}

export interface IStorefrontUpdateInput {
  tagline?: string;
  description?: string;
  collections?: IStorefrontCollectionInput[];
}

// The api rejects any property it does not know about, so every request body is
// assembled key by key here. Never pass form state straight into these methods.
const toCollectionPayload = (collection: IStorefrontCollectionInput) => {
  const payload: Record<string, any> = {
    name: collection.name.trim(),
    layout: collection.layout,
    order: collection.order,
    productIds: collection.productIds,
  };

  // Without an id the api creates a new collection.
  if (collection.id) {
    payload.id = collection.id;
  }

  if (collection.description !== undefined) {
    payload.description = collection.description.trim();
  }

  return payload;
};

export const StorefrontService = {
  getUserStorefront: async function (): Promise<IStorefront> {
    const { data } = await Api.get<IStorefront>(`/shop/storefront`);

    return data;
  },
  createUserStorefront: async function (createData: {
    tagline?: string;
    description: string;
    collections?: IStorefrontCollectionInput[];
  }): Promise<IStorefront> {
    const payload: Record<string, any> = {
      description: createData.description.trim(),
    };

    if (createData.tagline !== undefined) {
      payload.tagline = createData.tagline.trim();
    }

    // The id is dropped on purpose. The api looks every collection id up among
    // the storefront's existing collections, and one being created has none, so
    // any id here would fail the whole request.
    if (createData.collections !== undefined) {
      payload.collections = createData.collections.map((collection) =>
        toCollectionPayload({ ...collection, id: undefined }),
      );
    }

    const { data } = await Api.post<IStorefront>(`/shop/storefront`, payload);

    return data;
  },
  updateUserStorefront: async function (
    updateData: IStorefrontUpdateInput,
  ): Promise<IStorefront> {
    const payload: Record<string, any> = {};

    if (updateData.tagline !== undefined) {
      payload.tagline = updateData.tagline.trim();
    }

    if (updateData.description !== undefined) {
      payload.description = updateData.description.trim();
    }

    // Leaving collections out keeps the existing ones untouched, sending them
    // replaces the whole set.
    if (updateData.collections !== undefined) {
      payload.collections = updateData.collections.map(toCollectionPayload);
    }

    const { data } = await Api.patch<IStorefront>(`/shop/storefront`, payload);

    return data;
  },
  deleteUserStorefront: async function (): Promise<void> {
    await Api.delete(`/shop/storefront`);
  },
};
